import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { aiProviderApi, billingApi, AiProviderConfig, AiUsage, getAiProviderLabel, getAiProviderModel } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useIsAdmin } from '@/contexts/AuthContext';
import { AiCreditsModal } from '@/components/modals/AiCreditsModal';

const AI_FEATURE_ROWS: Array<{ key: keyof AiUsage; label: string }> = [
  { key: 'createAiUsed',  label: 'Create with AI' },
  { key: 'insightAiUsed', label: 'Item Insights' },
];

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  openrouter: 'OpenRouter',
};

const PROVIDER_MODELS: Record<string, string[]> = {
  anthropic: ['claude-haiku-4-5-20251001', 'claude-sonnet-4-6', 'claude-opus-4-6'],
  openai: ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo'],
  gemini: ['gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-2.0-flash'],
  openrouter: [],
};

function UsageRow({ used, label }: { used: number; label: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-text-secondary">{label}</span>
      <span className="font-medium text-text-primary">{used} {used === 1 ? 'use' : 'uses'}</span>
    </div>
  );
}

export function AiProviderTab() {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const setAiProvider = useAppStore((s) => s.setAiProvider);
  const setAiUsage = useAppStore((s) => s.setAiUsage);

  const [config, setConfig] = useState<AiProviderConfig | null>(null);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [creditsAdded, setCreditsAdded] = useState(false);
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  // Form state
  const [mode, setMode] = useState<'builtin' | 'custom'>('builtin');
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('claude-haiku-4-5-20251001');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyCleared, setApiKeyCleared] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadUsage = async () => {
    try {
      const u = await billingApi.getUsage();
      setUsage(u);
      setAiUsage(u);
    } catch {
      // non-fatal
    }
  };

  const handleResetUsage = async () => {
    if (!confirm('Reset all free-tier usage counters to 0? This is for testing only and cannot be undone.')) return;
    setResetting(true);
    try {
      await billingApi.resetUsage();
      setResetDone(true);
      setTimeout(() => setResetDone(false), 3000);
      await loadUsage();
    } catch {
      // non-fatal
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    Promise.all([
      aiProviderApi.get(),
      billingApi.getUsage(),
    ]).then(([cfg, u]) => {
      setConfig(cfg);
      setMode(cfg.mode);
      if (cfg.provider) setProvider(cfg.provider);
      if (cfg.model) setModel(cfg.model);
      if (cfg.apiKey) setApiKey(cfg.apiKey);
      setUsage(u);
      setAiUsage(u);
    }).catch(() => {}).finally(() => setIsLoading(false));
  }, []);

  // Detect Stripe success redirect
  useEffect(() => {
    if (location.search.includes('credits=added')) {
      setCreditsAdded(true);
      loadUsage();
      setTimeout(() => setCreditsAdded(false), 5000);
    }
  }, [location.search]);

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
    const models = PROVIDER_MODELS[newProvider];
    setModel(models?.length > 0 ? models[0] : '');
  };

  const handleClearApiKey = () => {
    setApiKey('');
    setApiKeyCleared(true);
  };

  const isMasked = (val: string) => /^•+.{4}$/.test(val);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError('');
    try {
      const payload: { mode: string; provider?: string; model?: string; apiKey?: string } = { mode };
      if (mode === 'custom') {
        payload.provider = provider;
        payload.model = model;
        if (!apiKeyCleared && apiKey) payload.apiKey = apiKey;
      }
      const updated = await aiProviderApi.update(payload);
      setConfig(updated);
      setAiProvider(getAiProviderLabel(updated), getAiProviderModel(updated));
      setMode(updated.mode);
      if (updated.provider) setProvider(updated.provider);
      if (updated.model) setModel(updated.model);
      if (updated.apiKey) { setApiKey(updated.apiKey); setApiKeyCleared(false); }
      else { setApiKey(''); setApiKeyCleared(false); }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      // Reload usage in case mode changed
      loadUsage();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-text-secondary text-sm">Loading...</p>
      </div>
    );
  }

  const models = PROVIDER_MODELS[provider] || [];
  const usageIsActive = mode === 'builtin';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">AI Provider</h2>
        <p className="text-sm text-text-secondary mt-0.5">How should Intelligence features be powered?</p>
      </div>

      {creditsAdded && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          ✓ Credits added successfully — your balance has been updated.
        </div>
      )}

      {/* Two-column layout */}
      <div className="flex gap-6 items-start">

        {/* Left: Provider config */}
        <div className="flex-1 bg-surface-default rounded-lg border border-border-default p-5 space-y-5">
          {/* Mode selection */}
          <div className="space-y-3">
            <label
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                mode === 'builtin' ? 'border-blue-500 bg-blue-50' : 'border-border-default hover:border-border-strong'
              }`}
              onClick={() => setMode('builtin')}
            >
              <div className="mt-0.5 flex-shrink-0">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${mode === 'builtin' ? 'border-blue-500' : 'border-border-strong'}`}>
                  {mode === 'builtin' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Built-in Claude</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Platform's Claude subscription. No setup required. Credit limits apply.
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                mode === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-border-default hover:border-border-strong'
              }`}
              onClick={() => setMode('custom')}
            >
              <div className="mt-0.5 flex-shrink-0">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${mode === 'custom' ? 'border-blue-500' : 'border-border-strong'}`}>
                  {mode === 'custom' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Custom</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Your own API key from any supported provider. No per-use charges.
                </p>
              </div>
            </label>
          </div>

          {/* Custom fields */}
          {mode === 'custom' && (
            <div className="space-y-4 pt-2 border-t border-border-subtle">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Provider</label>
                <select
                  value={provider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background-primary border border-border-default rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                >
                  {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Model</label>
                {models.length > 0 ? (
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background-primary border border-border-default rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  >
                    {models.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="e.g. anthropic/claude-3.5-sonnet"
                    className="w-full px-3 py-2 text-sm bg-background-primary border border-border-default rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  />
                )}
                {provider === 'openrouter' && (
                  <p className="text-xs text-text-secondary mt-1">
                    Enter any OpenRouter model ID (e.g. <code className="font-mono">anthropic/claude-3.5-sonnet</code>)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">API Key</label>
                <div className="flex gap-2">
                  <input
                    type={apiKey && isMasked(apiKey) ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => { setApiKey(e.target.value); setApiKeyCleared(false); }}
                    placeholder="Enter API key"
                    className="flex-1 px-3 py-2 text-sm bg-background-primary border border-border-default rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 font-mono"
                  />
                  {config?.apiKey && !apiKeyCleared && (
                    <button
                      type="button"
                      onClick={handleClearApiKey}
                      className="px-3 py-2 text-xs text-text-secondary border border-border-default rounded-md hover:text-text-primary hover:border-border-strong transition-colors whitespace-nowrap"
                    >
                      Clear to change
                    </button>
                  )}
                </div>
                <p className="text-xs text-text-secondary mt-1">Stored encrypted. Leave unchanged to keep current key.</p>
              </div>
            </div>
          )}

          {/* Save */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save AI Settings'}
            </button>
            {saved && <span className="text-sm text-green-600">Saved</span>}
            {saveError && <span className="text-sm text-red-600">{saveError}</span>}
          </div>
        </div>

        {/* Right: Usage panel */}
        <div className={`flex-1 transition-opacity ${usageIsActive ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <div className="bg-surface-default rounded-lg border border-border-default p-5 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">AI Usage</h3>
              {!usageIsActive && (
                <span className="text-xs text-text-tertiary italic">using your key</span>
              )}
            </div>

            {usage ? (
              <>
                {/* Feature usage counts */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Feature Usage</p>
                  {AI_FEATURE_ROWS.map((row) => (
                    <UsageRow
                      key={row.key}
                      used={usage[row.key] as number}
                      label={row.label}
                    />
                  ))}
                </div>

                {/* Credits remaining */}
                <div className="pt-3 border-t border-border-subtle space-y-2">
                  <div className="flex items-baseline justify-between">
                    <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Credits Remaining</p>
                    <span className={`text-lg font-bold ${usage.paidAiCredits === 0 ? 'text-red-600' : 'text-text-primary'}`}>
                      {usage.paidAiCredits}
                    </span>
                  </div>
                  <p className="text-xs text-text-tertiary">$0.10 / use · shared across all AI features</p>
                  {isAdmin && (
                    <button
                      onClick={() => setIsBuyModalOpen(true)}
                      className={`w-full py-2 text-sm rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors ${
                        usage.paidAiCredits === 0 ? 'font-medium ring-2 ring-amber-300/40' : 'font-normal'
                      }`}
                    >
                      Buy Credits
                    </button>
                  )}
                </div>

                {/* Recent transactions */}
                {usage.transactions.length > 0 && (
                  <div className="pt-3 border-t border-border-subtle space-y-2">
                    <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Recent Purchases</p>
                    <div className="space-y-1.5">
                      {usage.transactions.slice(0, 5).map((t) => (
                        <div key={t.id} className="flex items-center justify-between text-xs">
                          <span className="text-green-600 font-medium">+{t.credits} credits</span>
                          <span className="text-text-tertiary">
                            ${(t.amountCents / 100).toFixed(0)} · {new Date(t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Reset counters — for testing */}
                <div className="pt-3 border-t border-border-subtle">
                  <button
                    onClick={handleResetUsage}
                    disabled={resetting}
                    className="w-full py-1.5 text-xs text-text-tertiary border border-dashed border-border-subtle rounded-md hover:text-red-500 hover:border-red-300 transition-colors disabled:opacity-50"
                  >
                    {resetting ? 'Resetting…' : resetDone ? '✓ Counters reset' : 'Reset usage counters'}
                  </button>
                  <p className="text-xs text-text-tertiary mt-1 text-center">For testing only · does not restore credits</p>
                </div>
              </>
            ) : (
              <p className="text-xs text-text-tertiary">Usage data unavailable</p>
            )}
          </div>
        </div>
      </div>

      {/* Buy credits modal (also used from the panel) */}
      <AiCreditsModal
        isOpen={isBuyModalOpen}
        onClose={() => { setIsBuyModalOpen(false); loadUsage(); }}
        creditType="create"
      />
    </div>
  );
}
